import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink as RouterNavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@styles/theme';
import { Button } from '../common/Button';
import { Drawer } from '../common/Drawer';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { logout } from '@store/features/userSlice';
import { useCartGetQuery } from '@services/api';
import { clearAuthCookies, hasAuthToken, isGuestMode } from '@utils/cookie';

const HeaderContainer = styled.header`
  background-color: ${theme.colors.surface.glass};
  border-bottom: 1px solid ${theme.colors.border.light};
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: ${theme.zIndex.sticky};
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;

  @media (max-width: 375px) {
    padding: 0 ${theme.spacing.md};
  }
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  flex: 0 1 260px;
  max-width: 260px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 0 1 200px;
    max-width: 200px;
  }

  @media (max-width: 375px) {
    flex: 0 1 180px;
    max-width: 180px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  justify-content: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(RouterNavLink)`
  color: ${theme.colors.text.primary};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.body};
  font-weight: ${theme.typography.fontWeight.medium};
  transition: color ${theme.transitions.fast};
  position: relative;

  &:hover {
    color: ${theme.colors.primary};
  }

  &.active,
  &[aria-current='page'] {
    color: ${theme.colors.primary};
    &::after {
      content: '';
      position: absolute;
      bottom: -18px;
      left: 0;
      right: 0;
      height: 2px;
      background-color: ${theme.colors.primary};
    }
  }
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: 375px) {
    gap: ${theme.spacing.sm};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-height: 36px;
  padding: 0 ${theme.spacing.md};
  padding-left: ${theme.spacing.xl};
  border-radius: 0;
  border: none;
  background-color: ${theme.colors.surface.base};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  transition: box-shadow ${theme.transitions.fast},
    background-color ${theme.transitions.fast};

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.colors.focusRing};
  }
`;

const SearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 36px;
  border-radius: 0;
  border: none;
  background-color: ${theme.colors.surface.base};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background.secondary};
  }
`;

const SearchIcon = styled.svg`
  width: 18px;
  height: 18px;
  display: block;
  fill: ${theme.colors.text.primary};
`;

const SearchButtonText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const DesktopOnly = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  border: 1px solid ${theme.colors.border.default};
  background: ${theme.colors.surface.base};
  color: ${theme.colors.text.primary};
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.fontSize.lg};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: inline-flex;
  }
`;

const MobileMenuContent = styled.div`
  padding: ${theme.spacing.xl} ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const MobileMenuLink = styled(RouterNavLink)`
  color: ${theme.colors.text.primary};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.headline};
  font-weight: ${theme.typography.fontWeight.medium};
  padding: ${theme.spacing.sm} 0;
`;

const CartIcon = styled(Link)`
  position: relative;
  color: ${theme.colors.text.primary};
  text-decoration: none;
  font-size: ${theme.typography.fontSize.xl};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  transition: background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background.secondary};
  }
`;

const CartGlyph = styled.svg`
  width: 20px;
  height: 20px;
  display: block;
  fill: ${theme.colors.text.primary};
`;

const CartBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.text.inverse};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  padding: 2px 6px;
  border-radius: ${theme.borderRadius.full};
  min-width: 18px;
  text-align: center;
`;

export const Header: React.FC = () => {
  const { t } = useTranslation(['layout', 'products']);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const { currentUser } = useAppSelector((state) => state.user);
  const { data: cartData } = useCartGetQuery(undefined, {
    skip: !hasAuthToken(),
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const cartItemCount = (cartData?.items ?? []).reduce(
    (sum, item) => sum + Number(item.count ?? 0),
    0
  );

  const handleLogout = () => {
    clearAuthCookies();
    dispatch(logout());
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleSearchSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/products?keyword=${encodeURIComponent(value)}`);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <SearchForm role="search" onSubmit={handleSearchSubmit}>
          <SearchInput
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={t('searchPlaceholder', { ns: 'products' })}
            aria-label={t('searchPlaceholder', { ns: 'products' })}
          />
          <SearchButton type="submit">
            <SearchIcon
              aria-hidden="true"
              focusable="false"
              role="presentation"
              viewBox="0 0 20 20"
            >
              <path d="M18.64 17.02l-5.31-5.31c.81-1.08 1.26-2.43 1.26-3.87C14.5 4.06 11.44 1 7.75 1S1 4.06 1 7.75s3.06 6.75 6.75 6.75c1.44 0 2.79-.45 3.87-1.26l5.31 5.31c.45.45 1.26.54 1.71.09.45-.36.45-1.17 0-1.62zM3.25 7.75c0-2.52 1.98-4.5 4.5-4.5s4.5 1.98 4.5 4.5-1.98 4.5-4.5 4.5-4.5-1.98-4.5-4.5z" />
            </SearchIcon>
            <SearchButtonText>
              {t('searchButtonLabel', { ns: 'products' })}
            </SearchButtonText>
          </SearchButton>
        </SearchForm>
        <Nav>
          <NavLink to="/">{t('nav.home')}</NavLink>
          <NavLink to="/products">{t('nav.products')}</NavLink>
          <NavLink to="/company">{t('nav.company')}</NavLink>
        </Nav>
        <UserActions>
          <CartIcon to="/cart">
            <CartGlyph
              aria-hidden="true"
              focusable="false"
              role="presentation"
              viewBox="0 0 20 20"
            >
              <path d="M18.936 5.564c-.144-.175-.35-.207-.55-.207h-.003L6.774 4.286c-.272 0-.417.089-.491.18-.079.096-.16.263-.094.585l2.016 5.705c.163.407.642.673 1.068.673h8.401c.433 0 .854-.285.941-.725l.484-4.571c.045-.221-.015-.388-.163-.567z" />
              <path d="M17.107 12.5H7.659L4.98 4.117l-.362-1.059c-.138-.401-.292-.559-.695-.559H.924c-.411 0-.748.303-.748.714s.337.714.748.714h2.413l3.002 9.48c.126.38.295.52.942.52h9.825c.411 0 .748-.303.748-.714s-.336-.714-.748-.714zM10.424 16.23a1.498 1.498 0 1 1-2.997 0 1.498 1.498 0 0 1 2.997 0zM16.853 16.23a1.498 1.498 0 1 1-2.997 0 1.498 1.498 0 0 1 2.997 0z" />
            </CartGlyph>
            {cartItemCount > 0 && <CartBadge>{cartItemCount}</CartBadge>}
          </CartIcon>
          <DesktopOnly>
            {hasAuthToken() && (
              <NavLink to="/orders" style={{ fontSize: theme.typography.fontSize.sm }}>
                {t('nav.myOrders')}
              </NavLink>
            )}
            {currentUser && !isGuestMode() ? (
              <>
              <NavLink to="/addresses" style={{ fontSize: theme.typography.fontSize.sm }}>
                {t('nav.addresses')}
              </NavLink>
              <span>{currentUser?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                {t('logout', { ns: 'common' })}
              </Button>
              </>
            ) : (
              <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/login')}
              >
                {t('login', { ns: 'common' })}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/register')}
              >
                {t('register', { ns: 'common' })}
              </Button>
              </>
            )}
          </DesktopOnly>
          <MobileMenuButton onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
            ≡
          </MobileMenuButton>
        </UserActions>
      </HeaderContent>
      <Drawer isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} position="right">
        <MobileMenuContent>
          <MobileMenuLink to="/" onClick={() => setMobileMenuOpen(false)}>{t('nav.home')}</MobileMenuLink>
          <MobileMenuLink to="/products" onClick={() => setMobileMenuOpen(false)}>{t('nav.products')}</MobileMenuLink>
          <MobileMenuLink to="/company" onClick={() => setMobileMenuOpen(false)}>{t('nav.company')}</MobileMenuLink>
          {hasAuthToken() && (
            <MobileMenuLink to="/orders" onClick={() => setMobileMenuOpen(false)}>{t('nav.myOrders')}</MobileMenuLink>
          )}
          {currentUser && !isGuestMode() ? (
            <>
              <MobileMenuLink to="/addresses" onClick={() => setMobileMenuOpen(false)}>{t('nav.addresses')}</MobileMenuLink>
              <Button variant="ghost" onClick={handleLogout}>
                {t('logout', { ns: 'common' })}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                {t('login', { ns: 'common' })}
              </Button>
              <Button variant="primary" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>
                {t('register', { ns: 'common' })}
              </Button>
            </>
          )}
        </MobileMenuContent>
      </Drawer>
    </HeaderContainer>
  );
};
